"use client";

import { useEffect, useRef } from "react";

/**
 * A React hook that invokes a callback **only when the given value changes**
 * between renders. It skips the initial mount and compares the previous and
 * current values on every re-render.
 *
 * @template T
 * @param {T} value - The reactive value to watch for changes. Can be any type, but
 * note that comparison uses strict equality (`!==`), so new object or array
 * references will always trigger the callback.
 * @param {(prev: T, next: T) => void} callback - The function to call whenever
 * the value changes. Receives the previous and next values as arguments.
 *
 * @example
 * ```tsx
 * function Example({ count }: { count: number }) {
 *   useOnChange(count, (prev, next) => {
 *     console.log(`count changed from ${prev} → ${next}`)
 *   })
 *   return <div>{count}</div>
 * }
 * ```
 *
 * @remarks
 * - The hook intentionally **skips the initial render**, so `callback` will
 *   only run on subsequent updates.
 * - Equality check is **referential**, not deep — if you pass objects,
 *   ensure stable references (e.g. via `useMemo`) or write your own
 *   comparison logic inside the callback.
 * - This hook is useful for running **side effects** that should only occur
 *   when a specific value actually changes, not just when the component re-renders.
 */
export function useOnChange<T>(value: T, callback: (prev: T, next: T) => void) {
  const prev = useRef(value);

  useEffect(() => {
    if (prev.current !== value) {
      callback(prev.current, value);
      prev.current = value;
    }
  }, [value, callback]);
}
